/**
 * Phase 5 — persist encounter diagnoses on the current Visit Note draft.
 * Opening the workspace or searching ICD-10 does not create a Note.
 * localStorage remains a cache and is never deleted here.
 */
import { ICD10CM_SYSTEM } from "@/lib/diagnosisApi";
import { assertEncounterWritable } from "@/lib/encounterApi";
import {
  getVisitNoteType,
  listVisitNotes,
  runExclusiveVisitNote,
} from "@/lib/visitNoteSoap";
import { api } from "@/lib/orgApi";

export const DIAGNOSIS_NOTE_PERSIST_KEY = "emrbymd.v3.diagnoses.notePersist";

export function isDiagnosisNotePersistEnabled() {
  try {
    return localStorage.getItem(DIAGNOSIS_NOTE_PERSIST_KEY) !== "off";
  } catch {
    return true;
  }
}

export function toNoteVersionDiagnoses(items = []) {
  return (items || [])
    .filter((item) => item?.code)
    .map((item, index) => {
      const snapshot = {
        code: item.code,
        system: item.system || ICD10CM_SYSTEM,
        description: item.description || item.code,
        sequence: index + 1,
        isPrimary: item.isPrimary === true,
        source: item.source || "lookup",
      };
      if (item.diagnosisCodeId) snapshot.diagnosisCodeId = item.diagnosisCodeId;
      return snapshot;
    });
}

export function fromNoteVersionDiagnoses(diagnoses = []) {
  return (diagnoses || [])
    .filter((item) => item?.code)
    .slice()
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    .map((item) => ({
      code: item.code,
      system: item.system || ICD10CM_SYSTEM,
      description: item.description || item.code,
      isPrimary: item.isPrimary === true,
      source: item.source || "lookup",
      sequence: item.sequence,
      diagnosisCodeId: item.diagnosisCodeId,
    }));
}

export function chooseWorkingDiagnoses(noteDiagnoses, fallback = []) {
  const fromNote = fromNoteVersionDiagnoses(noteDiagnoses);
  if (fromNote.length) return fromNote;
  return fallback || [];
}

/**
 * Prefer NoteVersion diagnoses. If the Visit Note has none and this browser
 * still has a localStorage working list, persist that list to Notes once.
 */
export async function hydrateVisitDiagnoses(encounterId, noteId, fallback = []) {
  const noteState = await loadVisitNoteDiagnoses(encounterId, noteId);
  const fromNote = fromNoteVersionDiagnoses(noteState?.diagnoses);
  if (fromNote.length) return { ...noteState, diagnoses: fromNote, hydratedFromLocal: false };
  if ((fallback || []).length) {
    const result = await persistVisitDiagnoses({
      encounterId,
      diagnoses: fallback,
      noteId: noteId || noteState?.noteId || null,
    });
    return {
      noteId: result?.noteId || noteState?.noteId || noteId || null,
      versionId: result?.versionId || noteState?.versionId || null,
      diagnoses: fallback,
      hydratedFromLocal: true,
    };
  }
  return { ...noteState, diagnoses: [], hydratedFromLocal: false };
}

export async function loadVisitNoteDiagnoses(encounterId, noteId = null) {
  if (!encounterId || !isDiagnosisNotePersistEnabled()) return null;
  try {
    if (noteId) {
      const { data } = await api.get(`/notes/${noteId}`);
      const version = data.currentVersion;
      return {
        noteId: data.id,
        versionId: version?.id || null,
        versionStatus: version?.status || null,
        diagnoses: fromNoteVersionDiagnoses(version?.diagnoses),
      };
    }
    const items = await listVisitNotes(encounterId);
    if (!items.length) return null;
    const { data } = await api.get(`/notes/${items[0].id}`);
    const version = data.currentVersion;
    return {
      noteId: data.id,
      versionId: version?.id || null,
      versionStatus: version?.status || null,
      diagnoses: fromNoteVersionDiagnoses(version?.diagnoses),
    };
  } catch {
    return null;
  }
}

async function writeDiagnosesToDraft(noteId, versionId, diagnoses) {
  const { data } = await api.put(`/notes/${noteId}/versions/${versionId}`, {
    diagnoses,
  });
  return { noteId, versionId: data.id || versionId, updated: true, diagnoses: data.diagnoses };
}

async function persistDiagnosesOnVisitNote(encounterId, items, noteId = null) {
  const diagnoses = toNoteVersionDiagnoses(items);
  const visitType = await getVisitNoteType();
  const existing = noteId ? [{ id: noteId }] : await listVisitNotes(encounterId);
  if (existing.length) {
    const { data } = await api.get(`/notes/${existing[0].id}`);
    const version = data.currentVersion;
    if (version?.status === "draft" && version.id) {
      await assertEncounterWritable(data.encounterId, { versionKind: version.kind });
      return writeDiagnosesToDraft(data.id, version.id, diagnoses);
    }
    if (version?.status === "signed") {
      return { noteId: data.id, versionId: version.id, skipped: true, reason: "signed" };
    }
    await assertEncounterWritable(data.encounterId);
    if (!version) {
      const draft = await api.post(`/notes/${data.id}/versions`, { diagnoses });
      return { noteId: data.id, versionId: draft.data.id, created: true, diagnoses: draft.data.diagnoses };
    }
    return { noteId: data.id, versionId: version?.id || null, skipped: true, reason: "existing-visit-note" };
  }
  await assertEncounterWritable(encounterId);
  const created = await api.post("/notes", {
    encounterId,
    noteTypeId: visitType.id,
  });
  const createdId = created.data.id;
  const draft = await api.post(`/notes/${createdId}/versions`, {
    content: { format: "soap", text: "" },
    diagnoses,
  });
  return { noteId: createdId, versionId: draft.data.id, created: true, diagnoses: draft.data.diagnoses };
}

export async function persistVisitDiagnoses({ encounterId, diagnoses, noteId = null }) {
  if (!encounterId || !isDiagnosisNotePersistEnabled()) {
    return { skipped: true, reason: "disabled-or-missing-encounter" };
  }
  // Writability is asserted per branch below, because an open addendum draft stays
  // writable on a completed encounter while net-new content does not.
  return runExclusiveVisitNote(encounterId, () => persistDiagnosesOnVisitNote(encounterId, diagnoses, noteId));
}

/** Persist diagnoses onto the selected NoteVersion. Never creates a Note identity. */
export async function persistNoteDiagnoses(noteId, items) {
  if (!noteId) {
    return { skipped: true, reason: "missing-note" };
  }
  const diagnoses = toNoteVersionDiagnoses(items);
  const { data } = await api.get(`/notes/${noteId}`);
  const version = data.currentVersion;
  if (version?.status === "draft" && version.id) {
    await assertEncounterWritable(data.encounterId, { versionKind: version.kind });
    return writeDiagnosesToDraft(data.id, version.id, diagnoses);
  }
  await assertEncounterWritable(data.encounterId);
  if (!version) {
    const draft = await api.post(`/notes/${noteId}/versions`, { diagnoses });
    return { noteId: data.id, versionId: draft.data.id, created: true, diagnoses: draft.data.diagnoses };
  }
  return { skipped: true, reason: "signed", noteId: data.id, versionId: version.id };
}
